import { useEffect, useRef } from 'react'
import { useAuthStore } from '@/store/authStore'
import { isPublicPath } from '@/utils/publicPaths'
import { getApiBaseUrl } from '@/config/env'
import axios from 'axios'

const API_BASE_URL = getApiBaseUrl()

/**
 * Hook para refrescar automáticamente el token antes de que expire
 * Previene desconexiones de WebSocket por tokens expirados
 */
export function useTokenRefresh() {
  const { token, login, logout } = useAuthStore()
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    if (!token) {
      // Limpiar interval si no hay token
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current)
        refreshIntervalRef.current = null
      }
      return
    }

    // Si hay token pero el usuario o tenant no están hidratados, recuperar con /auth/me
    const hydrateProfile = async () => {
      const state = useAuthStore.getState()
      if (!state.user || !state.tenant) {
        try {
          const res = await axios.get(`${API_BASE_URL}/auth/me`, {
            headers: {
              Authorization: `Bearer ${token}`,
              ...(state.tenantId ? { 'x-tenant-id': state.tenantId } : {}),
            },
          })
          if (res.data?.user) {
            login({
              token,
              user: res.data.user,
              tenant: res.data.tenant || state.tenant,
              tenantId: res.data.tenant?.id || state.tenantId,
            })
          }
        } catch {
          // Si falla, el fallback de JWT en api.ts mantiene datos mínimos funcionales
        }
      }
    }
    hydrateProfile()

    // Función para refrescar el token
    const refreshToken = async () => {
      try {
        const storedRefreshToken = localStorage.getItem('refreshToken')
        if (!storedRefreshToken) {
          console.warn('[TokenRefresh] No refresh token available')
          return
        }

        console.log('[TokenRefresh] Refreshing token proactively...')
        
        const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
          refreshToken: storedRefreshToken,
        })

        const accessToken: string | undefined =
          response.data?.tokens?.accessToken || response.data?.accessToken
        const newRefreshToken: string | undefined =
          response.data?.tokens?.refreshToken || response.data?.refreshToken

        if (!accessToken) {
          throw new Error('No access token returned from refresh endpoint')
        }

        // Actualizar tokens (preservando estado de usuario y tenant)
        login({ token: accessToken })
        
        if (newRefreshToken) {
          localStorage.setItem('refreshToken', newRefreshToken)
        }

        console.log('[TokenRefresh] Token refreshed successfully')
      } catch (error: any) {
        console.error('[TokenRefresh] Failed to refresh token:', error)
        
        // Si el servidor indica 401 (refresh token revocado/inválido), hacer logout
        if (error?.response?.status === 401) {
          localStorage.removeItem('refreshToken')
          logout()
          if (!isPublicPath(window.location.pathname)) {
            window.location.replace('/login')
          }
        }
      }
    }

    // Refrescar token preventivamente cada 10 minutos (los tokens expiran en 15m)
    // Esto garantiza que el token siga válido de forma continua mientras el usuario está activo
    const REFRESH_INTERVAL = 10 * 60 * 1000 // 10 minutos

    // Programar refresh proactivo
    refreshIntervalRef.current = setInterval(refreshToken, REFRESH_INTERVAL)

    // Cleanup
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current)
        refreshIntervalRef.current = null
      }
    }
  }, [token, login, logout])
}
